/**
 * In-memory + sessionStorage cache of queries the user has just submitted.
 *
 * Why this exists: the homepage mobile overlay seeds itself from `search_logs`
 * (via `useInstantSearchSeed`). After the user submits a query that DB row
 * exists, but a fresh seed fetch only happens on mount/auth-change — so a
 * just-typed query wouldn't appear at the top of "recents" until the next
 * full reload. This store lets us prepend submitted queries to the seed
 * instantly, no roundtrip required, for both signed-in AND anonymous users.
 *
 * Backed by `sessionStorage` so a quick back-button + re-open still shows
 * the recent within the same browser tab session.
 */
const STORAGE_KEY = "atp:session_recents";
const MAX = 8;

type Listener = (queries: string[]) => void;
const listeners = new Set<Listener>();

function read(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((q): q is string => typeof q === "string") : [];
  } catch {
    return [];
  }
}

function write(queries: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queries));
  } catch {
    /* sessionStorage may be unavailable (private mode / quota) — ignore */
  }
}

export function getSessionRecents(): string[] {
  return read();
}

export function pushSessionRecent(query: string) {
  const trimmed = query.trim();
  if (!trimmed || trimmed.length < 2 || trimmed.length > 200) return;
  const current = read();
  const lower = trimmed.toLowerCase();
  // Move-to-front semantics: drop any case-insensitive dupe, then unshift.
  const next = [trimmed, ...current.filter((q) => q.toLowerCase() !== lower)].slice(0, MAX);
  write(next);
  listeners.forEach((fn) => {
    try { fn(next); } catch { /* noop */ }
  });
}

export function subscribeSessionRecents(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
